import vClickOutside from "v-click-outside";
import moment from "moment";
import debounce from "debounce";
import { createNamespacedHelpers } from "vuex";
import { checkIfRangeDisabled, nextDataRange, prevDataRange } from "./calendarValidations";

const { mapGetters: userGetters } = createNamespacedHelpers("user"),
	{
		mapGetters: filtersGetters,
		mapMutations: filtersMutations,
		mapActions: filtersActions,
	} = createNamespacedHelpers("filters"),
	DEBOUNCE_TIMEOUT = 1000,
	DAY = 1,
	DAYS_IN_WEEK = 7,
	ONE_WEEK = 1;

export default {
	/*
	 * The `local` prop is used so that when an action is done within this component,
	 * it doesn't affect the global (vuex) state. This means that it would $emit a component event,
	 * and not a vuex action or an $eventBus eventbundleRenderer.renderToStream
	 */
	props: ["properties", "period", "local"],

	data() {
		return {
			calendarReady: false,
			showCalendar: false,
			prevDisabled: false,
			nextDisabled: false,
			showMonth: false,
			date: {},
			mode: "",
			dateOfValidity: "",
			availableYears: [],
			lastMonthIndex: 12,

			maxDate: new Date(),
			attributes: [
				{
					key: "today",
					firstDayOfWeek: 2,
					dates: moment().format(),
				},
				{
					highlight: {
						fillMode: "light",
						contentClass: "unavailable",
					},
					dates: {
						start: "dateOfValidity",
						end: moment().format(),
					},
				},
			],
			locale: {
				firstDayOfWeek: 2,
				masks: { weekdays: "WWW" },
				id: localStorage.getItem("language") || "en",
			},
		};
	},

	computed: {
		...userGetters(["activeOutlet", "openClosedDays", "exceptionalClosedDays"]),

		...filtersGetters(["activePeriod", "activeDate"]),

		minDate() {
			return moment(this.activeOutlet.outlet_start_date).format();
		},
		computedActivePeriod,
		disabledDates,
	},

	watch: {
		date() {
			this.checkIfRangeDisabled("prev");
			this.checkIfRangeDisabled("next");
		},
		period() {
			this.setCalendar();
		},
	},

	directives: { clickOutside: vClickOutside.directive },

	methods: {
		setCalendar,
		toggleCalendar,
		closeCalendar,
		selectYear,
		selectMonth,
		updateCalendar,
		nextDataRange,
		prevDataRange,
		parseDateOfValidity,
		setDesiredPeriod,
		findLastMonthAndYear,
		updateLanguage,
		checkIfRangeDisabled,
		prepareMonthsList,
		reloadComponentData: debounce(reloadComponentData, DEBOUNCE_TIMEOUT),

		...filtersMutations(["setActiveDate"]),

		...filtersActions(["selectPeriod"]),
	},

	beforeMount() {
		this.setCalendar();
		this.$eventBus.$on("setCalendar", this.setCalendar);
		this.$eventBus.$on("updateLanguage", this.updateLanguage);
	},

	created() {
		this.date = this.activeDate.format("YYYY-MM-DD");
	},

	beforeDestroy() {
		this.$eventBus.$off("setCalendar", this.setCalendar);
		this.$eventBus.$off("updateLanguage", this.updateLanguage);
	},
};

function disabledDates() {
	const MONDAY = 1,
		SUNDAY = 7,
		exceptionClosedDays = this.exceptionalClosedDays.map((closeDay) => ({
			start: moment(closeDay.start_date).toDate(),
			end: moment(closeDay.end_date).toDate(),
		})),
		openClosedDays = this.openClosedDays.map((closeDay) => ({
			start: closeDay.start_date,
			end: closeDay.end_date || new Date(),

			weekdays: this.$utils
				.range(MONDAY, SUNDAY)

				/*
				 * [Monday,..., Sunday]
				 * [1, 2, 3, 4, 5, 6, 7]
				 * - leave only working days
				 */
				.filter((dayNumber) => !closeDay.days.includes(dayNumber))

				/*
				 * [Sunday,..., Saturday]
				 * [1, 2, 3, 4, 5, 6, 7]
				 * - adjust dayNumbers so it can be used in v-calendar disabled-dates
				 */
				.map(transformToVCalendarWeekday),
		}));

	return [...exceptionClosedDays, ...openClosedDays];
}

/**
 * v-calendar represents Sundays as 1 and Saturdays as 7
 * but our vuex module represent Mondays as 1 and Sundays as 7
 * This function will transform our dayNumber representation to v-calendar representation
 */
function transformToVCalendarWeekday(ourDayNumber) {
	const vCalendarWeekdayNumber = ourDayNumber + 1,
		V_CALENDAR_LAST_WEEKDAY = 7;

	if (vCalendarWeekdayNumber > V_CALENDAR_LAST_WEEKDAY) {
		const V_CALENDAR_SUNDAY = 1;
		return V_CALENDAR_SUNDAY;
	}

	return vCalendarWeekdayNumber;
}

function reloadComponentData() {
	this.$eventBus.$emit("reloadComponentData");
}

function updateLanguage() {
	this.locale.id = localStorage.getItem("language") || "en";
}

function updateCalendar(val) {
	if (this.mode === "date") this.date = moment(val.id).toDate();

	if (this.mode === "range") {
		this.date = {
			start: moment(val.id)
				.subtract(val.weekdayPosition - DAY, "days")
				.toDate(),
			end: moment(val.id)
				.add(DAYS_IN_WEEK - val.weekdayPosition, "days")
				.toDate(),
		};
	}

	if (this.local) this.$emit("input", val.id);
	else {
		this.setActiveDate(val.id);
		this.$eventBus.$emit("reloadComponentData");
	}

	this.showCalendar = false;
}

function closeCalendar() {
	this.showCalendar = false;
}

function toggleCalendar() {
	this.showCalendar = !this.showCalendar;
}

function selectYear(year) {
	if (this.mode === "month") return;

	this.date = moment(moment([year])).format("YYYY/MM/DD");

	if (this.local) this.$emit("input", this.date);
	else {
		this.setActiveDate(this.date);
		this.$eventBus.$emit("reloadComponentData");
	}

	this.showCalendar = false;
}

function selectMonth(month) {
	this.date = month;

	if (this.local) this.$emit("input", this.date);
	else {
		this.setActiveDate(this.date);
		this.$eventBus.$emit("reloadComponentData");
	}

	this.showCalendar = false;
}

function setCalendar() {
	this.calendarReady = false;

	// calculate available months and years

	this.findLastMonthAndYear();

	// init dateOfValidity as beginning for processing data part in the calendar

	this.parseDateOfValidity();

	/*
	 * use dateOfValidity to create referenceDate ,
	 * override it if exist in localStorage or take lastDayOfValidity if don't
	 */

	this.setDesiredPeriod();

	// prepare month list
	this.prepareMonthsList();

	// constrain arrow navigation after date is set
	this.checkIfRangeDisabled("prev");
	this.checkIfRangeDisabled("next");

	this.calendarReady = true;
}

function findLastMonthAndYear() {
	this.availableYears = [];
	const yearOfValidity = moment(this.activeOutlet.outlet_data_validity).year(),
		minYear = moment(this.minDate).year();

	for (let year = minYear; year <= yearOfValidity; year++) {
		this.availableYears.push({
			value: year,
			months: [
				// this is used for parsing, look prepareMonthsList method
				year + "-01-01",
				year + "-02-01",
				year + "-03-01",
				year + "-04-01",
				year + "-05-01",
				year + "-06-01",
				year + "-07-01",
				year + "-08-01",
				year + "-09-01",
				year + "-10-01",
				year + "-11-01",
				year + "-12-01",
			],
		});
	}

	// show years by descending order
	this.availableYears.reverse();
}

function parseDateOfValidity() {
	const dateOfValidity = this.activeOutlet.outlet_data_validity;

	if (this.computedActivePeriod === "week")
		this.attributes[1].dates.start = moment(moment(dateOfValidity).startOf("week")).add(DAY, "days").toDate();
	else this.attributes[1].dates.start = moment(dateOfValidity).add(DAY, "days").toDate();

	this.dateOfValidity = moment(moment(dateOfValidity).startOf("week")).toDate();
}

// eslint-disable-next-line complexity
function setDesiredPeriod() {
	let referenceDate = moment(this.attributes[1].dates.start).subtract(DAY, "days").format("YYYY/MM/DD");

	if (this.activeDate) referenceDate = this.activeDate;
	else {
		this.setActiveDate(referenceDate);
		referenceDate = moment(referenceDate);
	}

	switch (this.computedActivePeriod) {
		case "day":
			this.date = moment(referenceDate).toDate();
			this.mode = "date";
			break;

		case "week": {
			let startOfWeek = moment(referenceDate).startOf("isoWeek"),
				endOfWeek = moment(referenceDate).endOf("isoWeek");

			const daysThatHasDataCount = moment(this.dateOfValidity).diff(startOfWeek, "days");

			/*
			 * if dateOfValidity is 6 Dec 2018,
			 * and the beginning of the week is 3 Dec 2018,
			 * that is 3 days difference between 6 Dec and 3 Dec
			 * in that case,
			 * we don't have enough days to compare to previous week,
			 * so we select the previous week for comparison
			 */
			if (daysThatHasDataCount < DAYS_IN_WEEK) {
				startOfWeek = startOfWeek.subtract(ONE_WEEK, "week");
				endOfWeek = endOfWeek.subtract(ONE_WEEK, "week");
			}

			this.date = {
				start: startOfWeek.toDate(),
				end: endOfWeek.toDate(),
			};

			this.mode = "range";
			break;
		}
		case "month":
			this.date = moment(referenceDate);
			this.mode = "month";
			break;

		case "year":
			this.mode = "year";
			this.date = moment(referenceDate);
			break;
	}
}

function prepareMonthsList() {
	const maxMonthIndex = Number(moment(this.dateOfValidity).format("M")),
		minMonthIndex = Number(moment(this.minDate).format("M")),
		minMonthYear = Number(moment(this.minDate).format("YYYY")),
		ZERO = 0;

	if (this.availableYears.length > ZERO)
		this.availableYears[0].months = this.availableYears[0].months.slice(ZERO, maxMonthIndex);

	for (let index = 0; index < this.availableYears.length; index++) {
		const START = 0,
			DECREMENT = 1;

		if (this.availableYears[index].value === minMonthYear)
			this.availableYears[index].months.splice(START, minMonthIndex - DECREMENT);
	}
}

function computedActivePeriod() {
	return this.local ? this.period : this.activePeriod;
}
